import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ErrorService } from '../error';
import { NotificationsService } from '../notifications';

import { ButtonType } from 'app/shared/buttons';
import { ButtonInterface, ModalService } from 'app/shared/modal';

type TranslationParams = Record<string, number | string>;

export type DeleteProps = {
    title: {
        key?: string;
        params?: TranslationParams;
    };
    content?: {
        key?: string;
        params?: TranslationParams;
    };
    onConfirm: () => Promise<unknown>;
    route?: Array<string>;
    successMessage?: string;
    ignoreSuccessMessage?: boolean;
    deleteButtonText?: string;
    cancelButtonText?: string;
};

@Injectable()
export class ConfirmPromptService {
    private modalService = inject(ModalService);
    private router = inject(Router);
    private errorService = inject(ErrorService);
    private notificationService = inject(NotificationsService);

    /**
     * Creates a delete prompt modal with the specified properties.
     * @param props - The properties for the delete prompt.
     */
    createDeletePrompt(props: DeleteProps): void {
        const titleText = props.title?.key || 'delete_prompt.title';
        const contentText = props.content?.key || 'delete_prompt.content';

        const cancelBtn: ButtonInterface = {
            label: props.cancelButtonText || 'delete_prompt.cancel',
            type: ButtonType.text,
            callback: () => {
                this.modalService.closeModal();
            },
        };

        const confirmBtn: ButtonInterface = {
            label: props.deleteButtonText || 'delete_prompt.delete',
            destructive: true,
            saving: false,
            callback: async () => {
                cancelBtn.disabled = true;
                confirmBtn.saving = true;

                this.modalService.updateModal({ denyClose: true });

                try {
                    await props.onConfirm();

                    this.modalService.closeModal();

                    if (!props.ignoreSuccessMessage) {
                        this.notificationService.translateSuccess(props.successMessage || 'delete_prompt.success');
                    }

                    if (props.route) {
                        this.router.navigate(props.route);
                    }
                } catch (error: unknown) {
                    const customErrors = (error as { customErrors?: unknown })?.customErrors;
                    this.errorService.handleError(customErrors || error, 'error.delete');
                } finally {
                    cancelBtn.disabled = false;
                    confirmBtn.saving = false;

                    this.modalService.updateModal({ denyClose: false });
                }
            },
        };

        this.modalService.createModal({
            size: 'small',
            denyClose: false,
            contentItems: [
                {
                    ngClass: 'relax h2',
                    contentTranslationKey: titleText,
                    contentParams: props.title?.params,
                },
                {
                    ngClass: 'relax body-14 regular',
                    contentTranslationKey: contentText,
                    contentParams: props.content?.params,
                },
            ],
            buttons: [cancelBtn, confirmBtn],
        });
    }
}
